/**
 * The agentic function-calling loop.
 *
 * Drives a Gemini chat session: send the user message, execute any tool
 * calls the model requests, feed the results back, and repeat until the
 * model produces a plain-text reply (or the turn cap is reached).
 */

import type { ChatSession, FunctionCall, GenerateContentResult, Part } from "firebase/ai";
import { mergeGroundingSources, extractGroundingSources } from "./grounding";
import { findTool } from "./tools/registry";
import {
  buildSeatingSetupQuestionnairePayload,
  SEATING_SETUP_QUESTIONNAIRE_TOOL,
  type SeatingSetupQuestionnairePayload,
} from "./tools/questionnaireTools";
import type { SeatingSetupAnswers } from "@amazing-wedding/shared-types";
import type { AgentTurnResult, GroundingSource, ToolActivity, ToolContext } from "./types";

/**
 * Hard cap on tool-call iterations to prevent runaway loops. Raised from 6 to
 * give agentic workflows (e.g. seating setup, multi-step reads before a write)
 * room to finish. Bulk tools (create_guests_batch / create_tasks_batch) collapse
 * many entities into a single turn, so this ceiling is rarely approached.
 */
export const MAX_TURNS = 12;

export interface AgentCallbacks {
  /** Resolve `true` to allow a destructive tool, `false` to skip it. */
  requestConfirmation: (toolName: string, description: string) => Promise<boolean>;
  /** Resolve answers for interactive tools, or null if cancelled. */
  requestQuestionnaire: (
    toolName: string,
    payload: SeatingSetupQuestionnairePayload,
  ) => Promise<SeatingSetupAnswers | null>;
  /** Live label while a tool runs; cleared when idle. */
  onProgress?: (label: string | null) => void;
}

function fnResponse(name: string, response: Record<string, unknown>): Part {
  return { functionResponse: { name, response } };
}

function collectSources(result: GenerateContentResult, sources: GroundingSource[]): void {
  mergeGroundingSources(sources, extractGroundingSources(result));
}

/** @internal Exported for unit tests. */
export async function executeCall(
  call: FunctionCall,
  ctx: ToolContext,
  callbacks: AgentCallbacks,
  activities: ToolActivity[],
): Promise<Part> {
  const tool = findTool(call.name);
  const args = (call.args ?? {}) as Record<string, unknown>;

  if (!tool) {
    return fnResponse(call.name, { error: true, message: `Unknown tool "${call.name}".` });
  }

  if (tool.interactive) {
    let description = call.name;
    try {
      description = tool.describe(args, ctx);
    } catch {
      // best-effort
    }

    if (call.name === SEATING_SETUP_QUESTIONNAIRE_TOOL) {
      const intro = typeof args.intro === "string" ? args.intro : undefined;
      const payload = buildSeatingSetupQuestionnairePayload(ctx, intro);
      const answers = await callbacks.requestQuestionnaire(call.name, payload);
      if (!answers) {
        activities.push({ id: crypto.randomUUID(), label: description, outcome: "cancelled" });
        return fnResponse(call.name, {
          cancelled: true,
          reason: "The user cancelled the seating setup form.",
        });
      }
      activities.push({ id: crypto.randomUUID(), label: description, outcome: "done" });
      return fnResponse(call.name, { answers });
    }

    activities.push({ id: crypto.randomUUID(), label: description, outcome: "error" });
    return fnResponse(call.name, {
      error: true,
      message: `Unsupported interactive tool "${call.name}".`,
    });
  }

  let description = call.name;
  try {
    description = tool.describe(args, ctx);
  } catch {
    // describe() is best-effort — fall back to the raw tool name.
  }

  if (tool.destructive) {
    const approved = await callbacks.requestConfirmation(call.name, description);
    if (!approved) {
      activities.push({ id: crypto.randomUUID(), label: description, outcome: "cancelled" });
      return fnResponse(call.name, {
        cancelled: true,
        reason: "The user declined this action.",
      });
    }
  }

  callbacks.onProgress?.(description);
  try {
    const output = await tool.execute(args, ctx);
    activities.push({
      id: crypto.randomUUID(),
      label: description,
      outcome: output.error ? "error" : "done",
    });
    return fnResponse(call.name, output);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    activities.push({ id: crypto.randomUUID(), label: description, outcome: "error" });
    return fnResponse(call.name, { error: true, message });
  } finally {
    callbacks.onProgress?.(null);
  }
}

export async function runAgentTurn(
  chat: ChatSession,
  /** Plain text, or a multimodal parts array (text + inlineData for images/PDFs). */
  userContent: string | Part[],
  ctx: ToolContext,
  callbacks: AgentCallbacks,
): Promise<AgentTurnResult> {
  const activities: ToolActivity[] = [];
  const sources: GroundingSource[] = [];
  const ctxWithSources: ToolContext = {
    ...ctx,
    appendSources: (incoming) => mergeGroundingSources(sources, incoming),
  };

  try {
    let result = await chat.sendMessage(userContent);

    for (let turn = 0; turn < MAX_TURNS; turn++) {
      collectSources(result, sources);
      const calls = result.response.functionCalls();
      if (!calls || calls.length === 0) {
        return { text: result.response.text(), activities, sources };
      }
      const responseParts: Part[] = [];
      for (const call of calls) {
        responseParts.push(await executeCall(call, ctxWithSources, callbacks, activities));
      }
      result = await chat.sendMessage(responseParts);
    }

    collectSources(result, sources);
    const text =
      result.response.text() ||
      "Nie udało się dokończyć tej operacji w kilku krokach. Spróbuj sformułować prośbę inaczej.";
    return { text, activities, sources };
  } finally {
    callbacks.onProgress?.(null);
  }
}
